export interface IPackChatContext {
  heading?: string;
  line?: string;
  amount?: string;
}

export interface IPackChatUser {
  id: string;
  name: string;
  role: string;
}

export interface IPackChatSource {
  ai_script_run_id: string;
  ai_script_version_id: string | null;
  pay_period_id: string;
  captured_at: string;
  statement_sha256: string;
}

export interface IPackChatMessage {
  id: string;
  body: string;
  author: IPackChatUser | null;
  // Optional new fields keep older API deployments readable.
  author_type?: "human" | "ai";
  event_type?:
    | "manager_requested"
    | "manager_intervened"
    | "ai_disabled"
    | "ai_failed"
    // AI answered, but said its own answer left the question open, so the API
    // moved the conversation to a manager without waiting to be asked.
    | "ai_unresolved"
    | null;
  reply_to_id?: string | null;
  sources?: IPackChatSource[];
  created_at: string;
}

export const PACK_CHAT_STATUS = {
  open: "open",
  closed: "closed",
} as const;

export type PackChatStatus =
  (typeof PACK_CHAT_STATUS)[keyof typeof PACK_CHAT_STATUS];

export interface IPackChatThreadSummary {
  id: string;
  status: PackChatStatus;
  response_owner?: "ai" | "manager";
  needs_manager?: boolean;
  has_ai_messages?: boolean;
  ai_response_status?: "idle" | "pending" | "failed" | "stopped";
  context: IPackChatContext;
  ai_script_run_id: string;
  technician: IPackChatUser;
  created_by: IPackChatUser;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  closed_by: IPackChatUser | null;
  source_sheet: { name: string; access_key: string | null } | null;
  message_count: number;
  last_message: IPackChatMessage | null;
}

export interface IPackChatThreadDetail extends IPackChatThreadSummary {
  messages: IPackChatMessage[];
}

export interface IPackChatThreadsResponse {
  threads: IPackChatThreadSummary[];
  open_count: number;
  needs_manager_count?: number;
  ai_enabled?: boolean;
}

export interface IPackChatThreadDetailResponse {
  thread: IPackChatThreadDetail;
}

export interface IPackChatThreadSummaryResponse {
  thread: IPackChatThreadSummary;
}

export interface IPackChatReplyResponse {
  thread: IPackChatThreadSummary;
  message: IPackChatMessage;
}

export interface IPackChatStartOption {
  technician: IPackChatUser;
  /** Every run in the period this technician has a pack in. */
  ai_script_runs: { id: string }[];
}

export interface IPackChatStartOptionsResponse {
  start_options: IPackChatStartOption[];
}

export interface IPackChatTechnicianGroup {
  technician: IPackChatUser;
  threads: IPackChatThreadSummary[];
  openCount: number;
  hasAiMessages: boolean;
  needsManager: boolean;
  responseOwner: "ai" | "manager";
  lastUpdatedAt: string;
  lastMessageThread: IPackChatThreadSummary | undefined;
}

export interface IPackChatThreadOverview {
  counts: { all: number; open: number; closed: number };
  groups: IPackChatTechnicianGroup[];
}

export interface PackChatSourceAccess {
  businessId: string | null;
  /** Omitted for the technician's own authorized statement route. */
  technicianId?: string;
}
